/**
 * Karmic Path Analysis v2 — five-signal classical engine.
 *
 * Two layers:
 *   - Layer 1: plain-language takeaways (5 bullets, no jargon)
 *   - Layer 2: detailed signal-by-signal justification with the chart
 *     fact named (e.g. "Saturn in the 7th (R)")
 *
 * Methodology footer cites the classical source for each signal so
 * the reader can verify against BPHS / Jaimini Sutras / etc.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  ChartCreateRequest,
  KarmicLineEntry,
  KarmicPathResponse,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function KarmicPathPage() {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [submittedReq, setSubmittedReq] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<KarmicPathResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.karmicPath(req),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setSubmittedReq(req);
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
      if (e.status === 401) return t('फिर से प्रवेश करें', 'Please sign in again');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  const downloadPdf = useCallback(async () => {
    if (!submittedReq) return;
    try {
      setPdfDownloading(true);
      const blob = await api.karmicPathPdfBlob(submittedReq, lang);
      const name = (report?.input.name ?? 'karmic').replace(/\s+/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `karmic_path_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [submittedReq, lang, report, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('कर्म-पथ विश्लेषण', 'Karmic Path Analysis')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'पाँच शास्त्रीय संकेतों के आधार पर — केतु नक्षत्र, आत्म-कारक, कारकांश, शनि भाव, द्वादशेश।',
              'Based on five classical signals — Ketu nakshatra, Atma Karaka, Karakamsa, Saturn-house, 12th lord.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
          <button
            type="button"
            onClick={() => setLang('hi')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'hi'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'hi'}
          >
            हिन्दी
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'en'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'en'}
          >
            English
          </button>
        </div>
      </header>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('विश्लेषण हो रहा है…', 'Analysing…')}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
        />
      )}
    </div>
  );
}

// ─── Report renderer ────────────────────────────────────────────────────────

function ReportView({
  report,
  lang,
  t,
  onDownloadPdf,
  pdfDownloading,
}: {
  report: KarmicPathResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
}) {
  return (
    <article className="space-y-4">
      {/* Header card with PDF download */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('कर्म-पथ रिपोर्ट', 'Karmic Path Report')}
          </p>
          <p className="mt-1 text-xl font-semibold text-saffron-100">
            {report.input.name}
          </p>
          <p className="mt-0.5 text-xs text-stone-400">
            {report.input.dob} · {report.input.tob} ·{' '}
            {lang === 'hi' ? report.lagna.rashi_name_hi : report.lagna.rashi_name_en}{' '}
            {t('लग्न', 'Ascendant')}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={pdfDownloading}
          className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {pdfDownloading
            ? t('बन रहा है…', 'Generating…')
            : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </section>

      {/* ─── Layer 1 — plain-language takeaways ──────────────────────── */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('मुख्य निष्कर्ष', 'Key Takeaways')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'सरल भाषा में — ज्योतिषीय शब्दों के बिना',
            'In plain language — no astrological jargon',
          )}
        </p>
        <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
          {(lang === 'hi' ? report.takeaways_hi : report.takeaways_en).map((tk, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-saffron-400">●</span>
              <span>{tk}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ─── Layer 2 — detailed justification, signal by signal ─────── */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('विस्तृत विश्लेषण', 'Detailed Analysis')}
        </h2>
        <p className="mb-4 text-xs text-stone-500">
          {t(
            'पाँच शास्त्रीय संकेत — प्रत्येक का स्रोत मेथडोलॉजी में नीचे',
            'Five classical signals — sources cited in the methodology footer below',
          )}
        </p>
        <div className="space-y-3">
          <SignalCard line={report.ketu_signature} lang={lang} />
          <SignalCard line={report.atma_karaka_signature} lang={lang} />
          <SignalCard line={report.karakamsa_signature} lang={lang} />
          <SignalCard line={report.saturn_signature} lang={lang} />
          <SignalCard line={report.twelfth_signature} lang={lang} />
        </div>
      </section>

      {/* ─── Numerology (auxiliary, marked as Chaldean not Vedic) ───── */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('चाल्डियन अंक-शास्त्र', 'Chaldean Numerology')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'पूरक संकेत (वैदिक नहीं — चाल्डियन परम्परा से)',
            'Supplementary signal (not Vedic — from the Chaldean tradition)',
          )}
        </p>
        <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <NumberCell
            label={t('जन्म', 'Birth')}
            number={report.numerology.birth_number}
            total={report.numerology.birth_total}
            planet={lang === 'hi' ? report.numerology.birth_planet_hi : report.numerology.birth_planet_en}
          />
          <NumberCell
            label={t('नियति', 'Destiny')}
            number={report.numerology.destiny_number}
            total={report.numerology.destiny_total}
            planet={lang === 'hi' ? report.numerology.destiny_planet_hi : report.numerology.destiny_planet_en}
          />
          <NumberCell
            label={t('नाम', 'Name')}
            number={report.numerology.name_number}
            total={report.numerology.name_total}
            planet={lang === 'hi' ? report.numerology.name_planet_hi : report.numerology.name_planet_en}
          />
        </div>
        <ul className="mt-4 space-y-1 text-xs text-stone-300">
          {(lang === 'hi' ? report.numerology.summary_hi : report.numerology.summary_en).map((s, i) => (
            <li key={i}>· {s}</li>
          ))}
        </ul>
      </section>

      {/* ─── Chart structure (collapsible, for the curious) ─────────── */}
      <details className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <summary className="cursor-pointer text-sm font-medium text-saffron-200">
          {t('कुण्डली संरचना (विस्तार)', 'Chart Structure (expand)')}
        </summary>
        <table className="mt-3 w-full text-xs">
          <thead>
            <tr className="border-b border-stone-800 text-left text-saffron-300">
              <th className="py-1 pr-2">{t('भाव', 'House')}</th>
              <th className="py-1 pr-2">{t('राशि', 'Rashi')}</th>
              <th className="py-1">{t('ग्रह', 'Planets')}</th>
            </tr>
          </thead>
          <tbody>
            {report.structure.map((r) => (
              <tr key={r.house} className="border-b border-stone-900/50">
                <td className="py-1 pr-2 text-stone-400">{r.house}</td>
                <td className="py-1 pr-2 text-stone-200">
                  {lang === 'hi' ? r.rashi_hi : r.rashi_en}
                </td>
                <td className="py-1 text-stone-100">
                  {lang === 'hi' ? r.planets_hi : r.planets_en || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      {/* Methodology — financial-disclaimer tier (very small font) */}
      <section className="rounded-2xl bg-stone-900/20 p-3 ring-1 ring-stone-800/40">
        <h3 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
          {t('प्रयुक्त ज्योतिष-विधि', 'Methodology Used')}
        </h3>
        <ul className="space-y-0.5 text-[8.5px] leading-snug text-stone-500">
          {report.methods.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">
                {lang === 'hi' ? m.method_hi : m.method_en}
              </span>
              <span className="text-stone-600">
                {' — '}
                {lang === 'hi' ? m.source_hi : m.source_en}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-stone-800/40 pt-1.5 text-[8.5px] italic leading-snug text-stone-600">
          {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
        </p>
        <p className="mt-1 text-[8px] text-stone-700">
          {report.rules_version}
        </p>
      </section>
    </article>
  );
}

function SignalCard({
  line,
  lang,
}: {
  line: KarmicLineEntry;
  lang: 'hi' | 'en';
}) {
  return (
    <div className="rounded-md border-l-2 border-saffron-700/60 bg-stone-900/40 px-3 py-2.5">
      <p className="text-sm font-medium text-saffron-300">
        {lang === 'hi' ? line.label_hi : line.label_en}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? line.text_hi : line.text_en}
      </p>
    </div>
  );
}

function NumberCell({
  label,
  number,
  total,
  planet,
}: {
  label: string;
  number: number;
  total: number;
  planet: string;
}) {
  return (
    <div className="rounded-md bg-stone-900/60 p-3 ring-1 ring-stone-800">
      <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-saffron-200">{number}</p>
      <p className="text-[10px] text-stone-500">({total})</p>
      <p className="mt-1 text-xs text-stone-300">{planet}</p>
    </div>
  );
}
